/**
 * Folder-tab strip — the one place the workspace's folder-tab geometry lives
 * (the stage bar, the quote package tabs, the gear editor package tabs).
 * Previously hand-rolled three times with drifting class strings (sweep 2
 * Pass D candidate 11).
 *
 * The bar isolates its own stacking context: the selected tab's z-index (the
 * folder-merge border trick — the tab's bottom border paints over the bar's
 * hairline) must never escape the bar and paint above a section's
 * sent/approved mask, which is also z-10. QuoteSection learned this once;
 * the component applies it everywhere.
 *
 * `surface` names what the bar sits on, which decides both halves of the
 * folder illusion:
 * - `"page"` — the stone page (stage bar): the selected tab merges into the
 *   page background and unselected tabs are transparent text (DESIGN.md:
 *   only the selected tab is drawn).
 * - `"card"` — a white section card (package tabs): the selected tab merges
 *   into white and unselected tabs carry the gray fill.
 */
import type { ReactNode } from "react";
import { cx } from "@shared/cx";
import styles from "./FolderTabs.module.css";

type FolderTabSurface = "page" | "card";

interface FolderTabBarProps {
  ariaLabel: string;
  /** Package bars wrap on narrow screens; the stage bar never needs to. */
  wrap?: boolean;
  className?: string;
  children: ReactNode;
}

export function FolderTabBar({
  ariaLabel,
  wrap = false,
  className,
  children,
}: FolderTabBarProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(styles.bar, wrap && styles.barWrap, className)}
    >
      {children}
    </div>
  );
}

// `.tab` is the unselected state outright rather than a neutral base a
// variant tints: nothing merges, so a class that owns only half its look
// resolves by CSS source order instead of by intent.
function tabClasses(selected: boolean, surface: FolderTabSurface): string {
  if (selected) {
    return cx(
      styles.tab,
      styles.selected,
      surface === "page" ? styles.selectedPage : styles.selectedCard,
    );
  }
  return cx(styles.tab, surface === "card" && styles.unselectedCard);
}

interface FolderTabProps {
  selected: boolean;
  surface: FolderTabSurface;
  /**
   * Omit to render a non-button tab (a `span`) — for a selected tab whose
   * children are themselves interactive (the gear editor's rename input +
   * delete button), where nesting them in a button would be invalid.
   */
  onSelect?: () => void;
  children: ReactNode;
}

export function FolderTab({
  selected,
  surface,
  onSelect,
  children,
}: FolderTabProps) {
  if (!onSelect) {
    return (
      <span
        role="tab"
        aria-selected={selected}
        className={tabClasses(selected, surface)}
      >
        {children}
      </span>
    );
  }
  return (
    <button
      role="tab"
      type="button"
      aria-selected={selected}
      onClick={onSelect}
      className={cx(tabClasses(selected, surface), styles.clickable)}
    >
      {children}
    </button>
  );
}
